import {useInfiniteQuery} from "@tanstack/react-query";
import {API} from "api";

interface SceneCategory {
  id: string;
  label: string;
}

/** The GET /scene/list envelope: one page of the work-history listing. */
export interface ScenesPage {
  object: string;
  data: Array<{
    sceneId: string;
    prefix: string;
    sizeBytes: number;
    createdAt: string;
    title: string | null;
    context: {
      channel: SceneCategory | null;
      product: SceneCategory | null;
      type: SceneCategory | null;
    } | null;
  }>;
  has_more: boolean;
  next_cursor: string | null;
}

/**
 * Hold the envelope to its pagination contract: `has_more` true requires a
 * non-empty `next_cursor`, and `has_more` false requires a null cursor. A
 * malformed `{has_more: true, next_cursor: null}` reads as end-of-history and
 * hides scenes, so the mismatch throws here instead. queryFn awaits the check,
 * so a throw becomes a React Query error state.
 */
export function assertValidScenesPage(page: any): ScenesPage {
  if (
    !page ||
    typeof page !== "object" ||
    !Array.isArray(page.data) ||
    typeof page.has_more !== "boolean"
  ) {
    throw new Error("Scene list: malformed response envelope");
  }
  if (page.has_more) {
    if (typeof page.next_cursor !== "string" || !page.next_cursor) {
      throw new Error("Scene list: has_more is true but next_cursor is missing");
    }
  } else if (page.next_cursor !== null) {
    throw new Error("Scene list: has_more is false but next_cursor should be null");
  }
  return page as ScenesPage;
}

/**
 * React Query calls this rule synchronously during render, so the rule is total
 * and never throws. A null cursor becomes undefined, React Query's sentinel for
 * "no more pages", because a returned null would re-request the first page.
 */
export function getNextPageParam(lastPage: ScenesPage): string | undefined {
  return lastPage.next_cursor ?? undefined;
}

/**
 * The authenticated caller's saved scenes, newest first, paginated.
 *
 * The server scopes the listing to the session cookie, so the hook takes no user
 * argument and reaches no one else's work. `limit` sets the page size, and an
 * omitted `limit` takes the server default.
 */
export function useMyScenes({limit}: {limit?: number} = {}) {
  return useInfiniteQuery<ScenesPage>({
    queryKey: ["my-scenes", limit ?? null],
    queryFn: async ({pageParam}) =>
      assertValidScenesPage(
        await API.listMyScenes({cursor: pageParam as string | undefined, limit})
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    // React Query derives its default retry count from `typeof window`, so an
    // explicit count holds a test and the browser to one error path.
    retry: 1,
  });
}
